import { makeAutoObservable } from 'mobx';

import { UndoRedoStack } from './UndoRedoStack';
import { UndoRedoActionType } from './undoRedoTypes';

import type {
  IUndoRedoManager,
  UndoRedoCallable,
} from './undoRedoTypes';

/** Sentinel stack key used when no scene is loaded. */
export const UNDO_STACK_KEY_NONE = 'scene:__none__';

const LOG = true;

/**
 * Multi-stack undo/redo manager.
 *
 * Owns a keyed map of `UndoRedoStack` instances — one per scene path and one per
 * prefab isolation session. All `IUndoRedoManager` calls are delegated to the
 * currently active stack.
 *
 * Stack key conventions (always use the helper functions in `UndoRedoHelpers.ts`):
 * - `scene:<normalised-path>` — level scene undo history
 * - `prefab:<normalised-path>` — prefab isolation mode history
 * - `scene:__none__` — sentinel used when no scene is loaded
 *
 * Stacks are preserved when switching away, so navigating back to a previously
 * visited scene or reopening a prefab restores its full undo history.
 *
 * ## Lifecycle (managed by `UndoRedoHelpers.syncActiveUndoStack`)
 *
 * | Event | Action |
 * |-------|--------|
 * | `loadScene` completes | `setActiveStack(sceneKey)` — prior stack preserved |
 * | `resetScene` | `setActiveStack(UNDO_STACK_KEY_NONE)` |
 * | `openPrefab` | `setActiveStack(prefabKey)` — stack preserved across open/close |
 * | `closePrefab` | `setActiveStack(sceneKey)` |
 * | External prefab reload | `clearStack(prefabKey)` |
 * | External scene reload (file watcher) | `clearStack(sceneKey)` before reload |
 * | `closeProject` | `clearAllStacks()` |
 * | Scene file renamed | `renameStack(oldKey, newKey)` |
 */
export class UndoRedoManager implements IUndoRedoManager {
  private _stacks = new Map<string, UndoRedoStack>();
  private _activeStackKey = UNDO_STACK_KEY_NONE;
  /**
   * Bumped on every `setActiveStack` call so `getVersion()` always changes when
   * switching context, even when the incoming stack has the same numerical version
   * as the outgoing one (e.g. both freshly created at version 0).
   */
  private _switchVersion = 0;

  constructor() {
    makeAutoObservable(this);
    this._stacks.set(UNDO_STACK_KEY_NONE, new UndoRedoStack());
  }

  // === Multi-stack Management ===

  /**
   * Switches the active stack to `key`, creating a new empty stack if none exists for
   * that key. The previous stack is kept in the map and can be restored later.
   *
   * If the outgoing stack has an open group it is discarded with a warning (same
   * semantics as the pre-existing non-nested group rule).
   */
  setActiveStack(key: string): void {
    if (this._activeStackKey === key) return;

    if (LOG) console.log(`[UndoRedo] setActiveStack "${key}" (was "${this._activeStackKey}")`);

    const current = this._stacks.get(this._activeStackKey);
    if (current?.openGroup) {
      console.warn(`[UndoRedo] setActiveStack: discarding open group "${current.openGroup.name}" on stack "${this._activeStackKey}"`);
      current.openGroup = null;
    }

    if (!this._stacks.has(key)) {
      this._stacks.set(key, new UndoRedoStack());
    }
    this._activeStackKey = key;
    this._switchVersion++;
  }

  /**
   * Clears (resets) the history for a specific stack key without removing the stack
   * from the map. If the key has no stack, this is a no-op.
   *
   * Use this when external disk content invalidates a stack (e.g. file watcher reload,
   * external prefab reload).
   */
  clearStack(key: string): void {
    const stack = this._stacks.get(key);
    if (!stack) return;
    if (LOG) console.log(`[UndoRedo] clearStack "${key}"`);
    stack.clearHistory();
  }

  /**
   * Clears all stacks and resets to the initial sentinel state.
   * Call this when closing a project to avoid cross-project history leakage.
   */
  clearAllStacks(): void {
    if (LOG) console.log(`[UndoRedo] clearAllStacks (${this._stacks.size} stacks)`);
    for (const stack of this._stacks.values()) {
      stack.clearHistory();
    }
    this._stacks.clear();
    this._stacks.set(UNDO_STACK_KEY_NONE, new UndoRedoStack());
    this._activeStackKey = UNDO_STACK_KEY_NONE;
    this._switchVersion++;
  }

  /**
   * Renames a stack key, preserving its full history (e.g. when a scene file is renamed).
   * If `oldKey` has no stack, this is a no-op.
   */
  renameStack(oldKey: string, newKey: string): void {
    if (oldKey === newKey) return;
    const stack = this._stacks.get(oldKey);
    if (!stack) return;
    if (LOG) console.log(`[UndoRedo] renameStack "${oldKey}" → "${newKey}"`);
    this._stacks.set(newKey, stack);
    this._stacks.delete(oldKey);
    if (this._activeStackKey === oldKey) {
      // Same stack — no history change, just update the key reference.
      this._activeStackKey = newKey;
    }
  }

  /**
   * Returns the `IUndoRedoManager` for a specific stack key, creating the stack if it
   * does not yet exist. The returned reference always targets that fixed key regardless
   * of the currently active stack.
   *
   * Use this for operations that must always record to a specific context (e.g.
   * `getSceneUndoRedo()` records level-only changes to the scene stack even while
   * prefab isolation mode is active).
   */
  stackFor(key: string): IUndoRedoManager {
    if (!this._stacks.has(key)) {
      this._stacks.set(key, new UndoRedoStack());
    }
    return this._stacks.get(key)!;
  }

  /** The currently active stack key (observable). Useful for lifecycle hooks and tests. */
  get activeStackKey(): string {
    return this._activeStackKey;
  }

  // === IUndoRedoManager delegation — all calls forward to the active stack ===

  private get _activeStack(): UndoRedoStack {
    return this._stacks.get(this._activeStackKey)!;
  }

  beginGroup(name: string): void {
    this._activeStack.beginGroup(name);
  }

  endGroup(): void {
    this._activeStack.endGroup();
  }

  async recordAction(
    name: string,
    doMethod: UndoRedoCallable,
    undoMethod: UndoRedoCallable,
    actionType: UndoRedoActionType = UndoRedoActionType.Primary,
  ): Promise<void> {
    return this._activeStack.recordAction(name, doMethod, undoMethod, actionType);
  }

  /** @deprecated Use `recordAction` instead. */
  createAction(name: string, actionType?: UndoRedoActionType): void {
    this._activeStack.createAction(name, actionType);
  }

  /** @deprecated Use `recordAction` instead. */
  async commitAction(execute?: boolean): Promise<void> {
    return this._activeStack.commitAction(execute);
  }

  /** @deprecated Use `recordAction` instead. */
  setDoMethod(callable: UndoRedoCallable): void {
    this._activeStack.setDoMethod(callable);
  }

  /** @deprecated Use `recordAction` instead. */
  setUndoMethod(callable: UndoRedoCallable): void {
    this._activeStack.setUndoMethod(callable);
  }

  async undo(): Promise<boolean> {
    return this._activeStack.undo();
  }

  async redo(): Promise<boolean> {
    return this._activeStack.redo();
  }

  get isGroupOpen(): boolean {
    return this._activeStack.isGroupOpen;
  }

  hasUndo(): boolean {
    return this._activeStack.hasUndo();
  }

  hasRedo(): boolean {
    return this._activeStack.hasRedo();
  }

  isCommittingAction(): boolean {
    return this._activeStack.isCommittingAction();
  }

  isReplayingHistory(): boolean {
    return this._activeStack.isReplayingHistory();
  }

  /** Clears history on the active stack. For targeted clears, use `clearStack(key)`. */
  clearHistory(increaseVersion?: boolean): void {
    this._activeStack.clearHistory(increaseVersion);
  }

  getHistoryCount(): number {
    return this._activeStack.getHistoryCount();
  }

  getCurrentAction(): number {
    return this._activeStack.getCurrentAction();
  }

  getCurrentActionName(): string {
    return this._activeStack.getCurrentActionName();
  }

  getActionName(id: number): string {
    return this._activeStack.getActionName(id);
  }

  /**
   * Returns a version counter that increments whenever the active stack's history
   * changes OR the active stack itself switches. Used by reactive UI for change
   * detection (e.g. enabling/disabling undo/redo buttons).
   */
  getVersion(): number {
    return this._activeStack.version + this._switchVersion;
  }

  get maxSteps(): number {
    return this._activeStack.maxSteps;
  }

  set maxSteps(value: number) {
    this._activeStack.maxSteps = value;
  }
}
